import type { II18nRepository } from '../repositories/i18n.repository'
import type { LocaleMessages } from '../repositories/i18n.repository'

/** Код локали по умолчанию */
export const DEFAULT_LOCALE = 'en'

/**
 * Поддерживаемые локали
 * 
 * Примечания:
 * - 'ge' используется вместо стандартного ISO 639-1 кода 'ka' для грузинского языка
 *   для совместимости с существующими файлами локализации (ge.json)
 * - Стандартный ISO 639-1 код для грузинского языка - 'ka' (ქართული)
 * - 'ru-VS' исключена из списка, так как является временной и специфичной для переводов
 */
export const SUPPORTED_LOCALES = [
  'en',
  'uk',
  'ru',
  'bg',
  'fi',
  'hr',
  'sr',
  'lt',
  'hy',
  'pl',
  'ro',
  'af',
  'fr',
  'az',
  'es',
  'pt',
  'sq',
  'lv',
  'de',
  'bs',
  'ms',
  'th',
  'id',
  'it',
  'bn',
  'hi',
  'ta',
  'te',
  'mr', // Маратхи (Marathi) - मराठी
  'ml', // Малаялам (Malayalam) - മലയാളം
  'ht', // Гаитянский креольский (Haitian Creole) - Kreyòl ayisyen
  'ge'  // Грузинский (Georgian) - ქართული (используется 'ge' вместо стандартного 'ka' для совместимости)
] as const
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]

/**
 * Доменный сервис локализации.
 * Управляет выбором локали по умолчанию и загрузкой сообщений через репозиторий.
 */
export class I18nService {
  private static instance: I18nService | null = null

  constructor(private readonly i18nRepository: II18nRepository) {}

  static getInstance(i18nRepository: II18nRepository): I18nService {
    if (!I18nService.instance) {
      I18nService.instance = new I18nService(i18nRepository)
    }
    return I18nService.instance
  }

  static resetInstance(): void {
    I18nService.instance = null
  }

  /** Локаль по умолчанию */
  getDefaultLocale(): string {
    return DEFAULT_LOCALE
  }

  /** Список поддерживаемых локалей */
  getSupportedLocales(): readonly string[] {
    return SUPPORTED_LOCALES
  }

  /**
   * Загрузить сообщения локали по умолчанию
   */
  async loadDefaultLocale(): Promise<LocaleMessages> {
    return this.i18nRepository.loadMessages(DEFAULT_LOCALE)
  }

  /**
   * Загрузить сообщения для указанной локали
   */
  async loadLocale(locale: string): Promise<LocaleMessages> {
    return this.i18nRepository.loadMessages(locale)
  }
}
